import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, AlertTriangle, XCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { FindoSpinner } from "@/components/FindoSpinner";

const REJECT_PRESETS = ["vq.rejectNoMatch", "vq.rejectVague", "vq.rejectReturned", "vq.rejectOther"];

// Reusable rejection modal: preset reason chips + free-text, mirrors the Verifier
// screen. Calls onConfirm(reasonString).
export function RejectReasonModal({ open, onOpenChange, onConfirm, busy, title }) {
  const { t } = useLang();
  const [selected, setSelected] = useState(null);
  const [other, setOther] = useState("");
  const isOther = selected === "vq.rejectOther";
  const reason = isOther ? other.trim() : (selected ? t(selected) : "");
  const canConfirm = selected && (!isOther || other.trim());
  const reset = () => { setSelected(null); setOther(""); };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onOpenChange(false); } }}>
      <DialogContent className="sm:max-w-md" data-testid="reject-reason-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-findo-navy flex items-center gap-1.5">
            <XCircle size={18} className="text-red-500" /> {title || t("vq.rejectTitle")}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500 -mt-1">{t("vq.rejectHint")}</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {REJECT_PRESETS.map((r) => (
            <button key={r} type="button" onClick={() => setSelected(r)} disabled={busy}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${selected === r ? "border-findo-navy bg-findo-navy/10 text-findo-navy" : "border-findo-navy/20 text-findo-navy hover:bg-findo-navy/5 hover:border-findo-navy"}`}
              data-testid={`reject-preset-${r.replace("vq.reject", "").toLowerCase()}`}>
              {t(r)}
            </button>
          ))}
        </div>
        {isOther && (
          <Textarea rows={3} maxLength={300} value={other} onChange={(e) => setOther(e.target.value)}
            placeholder={t("vq.rejectOtherPh")} className="mt-3 resize-none" data-testid="reject-other-text" autoFocus />
        )}
        <div className="mt-4 flex flex-col gap-2">
          <Button onClick={() => onConfirm(reason)} disabled={busy || !canConfirm}
            className="rounded-full font-semibold bg-red-600 hover:bg-red-700" data-testid="reject-confirm-btn">
            {busy ? <><FindoSpinner size={16} className="mr-1.5" /> {t("vq.sending")}</> : t("vq.rejectConfirm")}
          </Button>
          <Button onClick={() => { reset(); onOpenChange(false); }} disabled={busy} variant="ghost"
            className="rounded-full font-semibold text-slate-500" data-testid="reject-cancel-btn">
            {t("vq.cancel")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Declarant side: define up to 3 identity-verification questions. `questions`
// is an array of strings; `setQuestions` receives the updated array.
export function VerificationQuestionsEditor({ questions, setQuestions, label, hint }) {
  const { t } = useLang();
  const list = [questions[0] || "", questions[1] || "", questions[2] || ""];
  const allEmpty = list.every((q) => !q.trim());
  const update = (i, v) => { const next = [...list]; next[i] = v; setQuestions(next); };
  return (
    <div data-testid="verification-questions-editor">
      <label className="text-sm font-semibold text-findo-navy flex items-center gap-1.5">
        <ShieldCheck size={15} /> {label || t("vq.title")}
        <span className="text-slate-400 font-normal">{t("vq.optional")}</span>
      </label>
      <p className="text-xs text-slate-500 mt-0.5">{hint || t("vq.hint")}</p>
      <div className="mt-2 space-y-2">
        {list.map((q, i) => (
          <Input key={i} value={q} maxLength={200} placeholder={`${t("vq.question").replace("{n}", i + 1)}${i > 0 ? ` ${t("vq.optionalSuffix")}` : ""}`}
            onChange={(e) => update(i, e.target.value)} data-testid={`verification-question-${i}`} />
        ))}
      </div>
      {allEmpty && (
        <div className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800" data-testid="verification-questions-warning">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>{t("vq.noneWarning")}</span>
        </div>
      )}
    </div>
  );
}

// Claimant side: answer the declarant's questions (or, if none, describe 2-3
// characteristics freely). Calls onSubmit([{question, answer}]).
export function VerificationAnswersForm({ questions, onSubmit, onCancel, busy }) {
  const { t } = useLang();
  const qs = (questions && questions.length) ? questions : null;
  const slots = qs || ["", "", ""];
  const [answers, setAnswers] = useState(slots.map(() => ""));
  const setA = (i, v) => { const n = [...answers]; n[i] = v; setAnswers(n); };
  const canSubmit = answers.some((a) => a.trim());
  const submit = () => {
    const payload = slots
      .map((q, i) => ({ question: qs ? q : "", answer: (answers[i] || "").trim() }))
      .filter((a) => a.answer);
    onSubmit(payload);
  };
  return (
    <div className="w-full max-w-md mx-auto" data-testid="verification-answers-form">
      <div className="rounded-xl bg-findo-navy/[0.04] border border-findo-navy/15 p-2.5 mb-2.5 flex items-start gap-2">
        <ShieldCheck size={16} className="text-findo-navy mt-0.5 shrink-0" />
        <p className="text-xs text-slate-600">
          {t(qs ? "vq.answersIntroQ" : "vq.answersIntroFree")}
        </p>
      </div>
      <div className="space-y-2">
        {slots.map((q, i) => (
          <div key={i}>
            <label className="text-sm font-semibold text-slate-700">
              {qs ? q : `${t("vq.characteristic").replace("{n}", i + 1)}${i > 0 ? ` ${t("vq.optionalSuffix")}` : ""}`}
              {qs && i === 0 && <span className="text-findo-red"> *</span>}
            </label>
            <Input maxLength={500} value={answers[i]} onChange={(e) => setA(i, e.target.value)}
              placeholder={qs ? t("vq.answerPh") : t("vq.charPh")}
              className="mt-1 h-9" data-testid={`verification-answer-${i}`} />
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-col gap-1.5">
        <Button onClick={submit} disabled={busy || !canSubmit}
          className="rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="verification-answers-submit">
          {busy ? <><FindoSpinner size={16} className="mr-1.5" /> {t("vq.sending")}</> : t("vq.submit")}
        </Button>
        {onCancel && (
          <Button onClick={onCancel} disabled={busy} variant="ghost" className="rounded-full font-semibold text-slate-500" data-testid="verification-answers-cancel">
            {t("vq.cancel")}
          </Button>
        )}
      </div>
    </div>
  );
}
